import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import Button from "@mui/material/Button";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useProduct from "../hooks/useProduct";
import useProductMedia from "../hooks/useProductMedia";
import useBookmarks from "../hooks/useBookmarks";
import useImagePrefetch from "../hooks/useImagePrefetch";

function formatSizeLineDisplay(line) {
  if (line == null || typeof line !== "string") return line;
  let s = line.trim();
  s = s.replace(/(\d+)\s*"\s*([xX])\s*(\d+)\s*"/g, '$1" $2 $3"');
  s = s.replace(/["“”](\d+)/g, "$1");
  return s;
}

const ZOOM_SCALE = 2.5;
const LENS_SIZE_FRAC = 0.35;

function getContainedImageRect(containerEl, naturalW, naturalH) {
  if (!containerEl || !naturalW || !naturalH) return null;
  const cw = containerEl.clientWidth;
  const ch = containerEl.clientHeight;
  const scale = Math.min(cw / naturalW, ch / naturalH);
  const rw = naturalW * scale;
  const rh = naturalH * scale;
  const ox = (cw - rw) / 2;
  const oy = (ch - rh) / 2;
  return { ox, oy, rw, rh };
}

function SingleProductPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const imageContainerRef = useRef(null);
  const [zoomActive, setZoomActive] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [lensCenter, setLensCenter] = useState({ cx: 0, cy: 0 });
  const [imageRect, setImageRect] = useState(null);

  const url = useLocation();
  const segments = url.pathname.split("/");
  const productId = segments[segments.length - 1];
  const navigationState = url.state;
  const categoryFromNav = navigationState?.category || "";

  const [category] = useState(categoryFromNav);
  const [activeMedia, setActiveMedia] = useState(null);
  const [sliderStart, setSliderStart] = useState(0);

  const { isBookmarked, toggle: handleToggleBookmark } = useBookmarks();

  const { data: productData } = useProduct(productId);
  const product = productData?.products?.[0] || null;
  const productsCatSlug = productData?.products_cat_slug || null;
  const TopLayerProduct = productData || null;
  const relatedProducts = product?.suggested_pairings || null;

  const { data: productMedia } = useProductMedia(productId);

  const sizeDisplay = useMemo(() => {
    if (!product) return { kind: "empty" };

    const stripTags = (value = "") =>
      String(value)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .trim();

    const addChunkedValues = (source, collector) => {
      if (!source) return;
      const values = Array.isArray(source) ? source : [source];
      values.forEach((entry) => {
        const clean = stripTags(entry);
        if (!clean) return;
        clean
          .split(/[\n,;|]+/)
          .map((item) => item.trim())
          .filter(Boolean)
          .forEach((item) => collector.push(item));
      });
    };

    const hasNumericSize = (value = "") => {
      const normalized = String(value)
        .toLowerCase()
        .replace(/[“”]/g, '"')
        .replace(/[′’]/g, "'")
        .replace(/\s+/g, " ")
        .trim();
      return /\d/.test(normalized);
    };

    const dedupeLines = (lines) => {
      const seen = new Set();
      return lines.filter((line) => {
        const key = line.toLowerCase().replace(/\s+/g, " ").trim();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    const fromProductSize = [];
    addChunkedValues(product?.product_size, fromProductSize);
    const productSizeDeduped = dedupeLines(fromProductSize);
    const isSpandexCategory = productsCatSlug === "spandex-products";
    if (isSpandexCategory && productSizeDeduped.length > 0) {
      return { kind: "list", items: productSizeDeduped };
    }

    const singles = Array.isArray(product?.single_products)
      ? [...product.single_products].sort(
          (a, b) => Number(a?.ID || 0) - Number(b?.ID || 0)
        )
      : product?.single_products;
    if (Array.isArray(singles) && singles.length > 0) {
      const linesFromSingles = [];
      singles.forEach((single) => {
        const title = stripTags(single?.post_title || "");
        if (!title) return;
        const hyphenIndex = title.indexOf("-");
        const displayPart =
          hyphenIndex !== -1
            ? title.substring(0, hyphenIndex).trim()
            : title.trim();
        if (!displayPart) return;
        if (isSpandexCategory || hasNumericSize(displayPart)) {
          linesFromSingles.push(displayPart);
        }
      });
      const singlesDeduped = dedupeLines(linesFromSingles);
      if (singlesDeduped.length > 0) {
        return { kind: "list", items: singlesDeduped };
      }
    }

    const productSizeWithNumeric = dedupeLines(
      fromProductSize.filter((line) => hasNumericSize(line))
    );
    if (productSizeWithNumeric.length > 0) {
      return { kind: "list", items: productSizeWithNumeric };
    }

    const detailText = TopLayerProduct?.detail_short_description;
    if (detailText != null && String(detailText).trim() !== "") {
      return { kind: "detail", text: String(detailText).trim() };
    }

    return { kind: "empty" };
  }, [product, TopLayerProduct, productsCatSlug]);

  const mediaUrls = useMemo(() => {
    const urls = [];
    if (product?.featured_image) urls.push(product.featured_image);
    if (productMedia?.gallery_images) urls.push(...productMedia.gallery_images);
    return urls;
  }, [product, productMedia]);
  useImagePrefetch(mediaUrls);

  const relatedImageUrls = useMemo(
    () => (relatedProducts || []).map((p) => p?.featured_image).filter(Boolean),
    [relatedProducts]
  );
  useImagePrefetch(relatedImageUrls);

  useEffect(() => {
    setActiveMedia(null);
    setSliderStart(0);
    setZoomActive(false);
    setNaturalSize({ w: 0, h: 0 });
  }, [productId]);

  useEffect(() => {
    setZoomActive(false);
    setNaturalSize({ w: 0, h: 0 });
  }, [activeMedia]);

  const mainImageSrc =
    activeMedia && activeMedia !== "main"
      ? activeMedia
      : product?.featured_image;

  const updateLensFromEvent = useCallback(
    (e) => {
      if (!isMdUp || !imageContainerRef.current || !naturalSize.w) return;
      const el = imageContainerRef.current;
      const ir = getContainedImageRect(el, naturalSize.w, naturalSize.h);
      if (!ir) return;
      const { ox, oy, rw, rh } = ir;
      const bounds = el.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const y = e.clientY - bounds.top;
      const lensW = rw * LENS_SIZE_FRAC;
      const lensH = rh * LENS_SIZE_FRAC;
      const cx = Math.max(ox + lensW / 2, Math.min(ox + rw - lensW / 2, x));
      const cy = Math.max(oy + lensH / 2, Math.min(oy + rh - lensH / 2, y));
      setLensCenter({ cx, cy });
      setImageRect(ir);
    },
    [isMdUp, naturalSize.w, naturalSize.h]
  );

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (!isMdUp || !mainImageSrc) return;
    setZoomActive(true);
    updateLensFromEvent(e);
  };

  const handleZoomWrapperLeave = () => setZoomActive(false);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setNaturalSize({ w: naturalWidth, h: naturalHeight });
    }
  };

  useEffect(() => {
    if (
      !zoomActive ||
      !isMdUp ||
      !naturalSize.w ||
      !imageContainerRef.current
    )
      return;
    const ir = getContainedImageRect(
      imageContainerRef.current,
      naturalSize.w,
      naturalSize.h
    );
    if (!ir) return;
    setLensCenter({ cx: ir.ox + ir.rw / 2, cy: ir.oy + ir.rh / 2 });
    setImageRect(ir);
  }, [naturalSize.w, naturalSize.h, zoomActive, isMdUp]);

  if (!product) {
    return (
      <Box sx={{ maxWidth: "1440px", margin: "0 auto" }}>
        <Box
          sx={{
            height: "70vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  const breadcrumbCaret = (
    <svg
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_2087_1613)">
        <path
          d="M3.38288 9.62958C3.60747 9.85416 3.96955 9.85416 4.19413 9.62958L8.00288 5.82083C8.18163 5.64208 8.18163 5.35333 8.00288 5.17458L4.19413 1.36583C3.96955 1.14124 3.60747 1.14124 3.38288 1.36583C3.1583 1.59041 3.1583 1.95249 3.38288 2.17708L6.70122 5.49999L3.3783 8.82291C3.1583 9.04291 3.1583 9.40958 3.38288 9.62958Z"
          fill="#7D7D7D"
        />
      </g>
      <defs>
        <clipPath id="clip0_2087_1613">
          <rect width="11" height="11" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  return (
    <Box>
      <Box sx={{ maxWidth: "1440px", margin: "0 auto" }}>
        <Box
          sx={{
            paddingInline: { xs: "16px", md: "60px" },
            paddingTop: { xs: "16px", md: "0px" },
            paddingBottom: "20px",
          }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "12px", md: "16px" },
              lineHeight: "20px",
              color: "#7D7D7D",
              letterSpacing: "-2%",
              fontWeight: 400,
              fontFamily: "Avenir Book",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}>
            <Typography
              component="span"
              onClick={() => navigate("/")}
              sx={{
                color: "#7D7D7D",
                fontFamily: "Avenir Book",
                fontWeight: 400,
                fontSize: { xs: "12px", md: "16px" },
                lineHeight: "20px",
                cursor: "pointer",
                "&:hover": { color: "#000000" },
              }}>
              Category
            </Typography>
            <Typography component="span">{breadcrumbCaret}</Typography>
            {category && (
              <>
                <Typography
                  component="span"
                  onClick={() => navigate(`/?key=${category}`)}
                  sx={{
                    color: "#7D7D7D",
                    fontWeight: 400,
                    fontSize: { xs: "12px", md: "16px" },
                    lineHeight: "20px",
                    cursor: "pointer",
                    fontFamily: "Avenir Book",
                    "&:hover": { color: "#000000" },
                  }}>
                  {category
                    .replace(/-/g, " ")
                    .split(" ")
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(" ")}
                </Typography>
                <Typography component="span">{breadcrumbCaret}</Typography>
              </>
            )}
            <Typography
              component="span"
              sx={{
                color: "#000000",
                fontWeight: 400,
                fontFamily: "Avenir Book",
                fontSize: { xs: "14px", md: "16px" },
                lineHeight: "20px",
              }}>
              {product?.name}
            </Typography>
          </Typography>
        </Box>
        <Grid
          container
          sx={{
            mt: 1,
            paddingBottom: "60px",
            paddingInline: { xs: "16px", md: "60px" },
          }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: "16px",
              flexDirection: { xs: "column", md: "row" },
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: "22px",
              }}>
              {productMedia &&
                (() => {
                  const galleryImages = productMedia.gallery_images || [];
                  const uniqueAltImages = galleryImages.filter(
                    (item) => item && item !== product?.featured_image
                  );
                  const mediaItems = [
                    { type: "main", src: product?.featured_image },
                    ...uniqueAltImages.map((item) => ({
                      type: "image",
                      src: item,
                    })),
                  ].filter((item) => item.src);
                  const hasAlternates = mediaItems.length > 1;
                  if (!hasAlternates) return null;
                  const THUMB_HEIGHT = 95;
                  const THUMB_GAP = 8;
                  const VISIBLE_COUNT = 3;
                  const visibleThumbs = Math.min(
                    mediaItems.length,
                    VISIBLE_COUNT
                  );
                  const canScroll = mediaItems.length > VISIBLE_COUNT;
                  const canScrollUp = canScroll && sliderStart > 0;
                  const canScrollDown =
                    canScroll &&
                    sliderStart < mediaItems.length - VISIBLE_COUNT;

                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "110px",
                        flexShrink: 0,
                        gap: "8px",
                      }}>
                      {canScroll && (
                        <Box
                          onClick={() =>
                            canScrollUp && setSliderStart((p) => p - 1)
                          }
                          sx={{
                            cursor: canScrollUp ? "pointer" : "default",
                            opacity: canScrollUp ? 1 : 0.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#f0f0f0",
                            transition: "opacity 0.2s, background-color 0.2s",
                            "&:hover": canScrollUp
                              ? { backgroundColor: "#e0e0e0" }
                              : {},
                          }}>
                          <KeyboardArrowUpIcon
                            sx={{ fontSize: 20, color: "#323232" }}
                          />
                        </Box>
                      )}

                      <Box
                        sx={{
                          height: `${
                            visibleThumbs * THUMB_HEIGHT +
                            2 +
                            (visibleThumbs - 1) * THUMB_GAP
                          }px`,
                          overflow: "hidden",
                          width: "100%",
                          flexShrink: 0,
                        }}>
                        <Box
                          sx={{
                            display: "flex",
                            paddingInline: "10px",
                            flexDirection: "column",
                            gap: `${THUMB_GAP}px`,
                            transform: `translateY(-${
                              sliderStart * (THUMB_HEIGHT + THUMB_GAP)
                            }px)`,
                            transition:
                              "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}>
                          {mediaItems.map((item, globalIndex) => {
                            const isMain = item.type === "main";
                            const isActive = isMain
                              ? !activeMedia || activeMedia === "main"
                              : activeMedia === item.src;

                            return (
                              <Box
                                key={globalIndex}
                                onClick={() =>
                                  setActiveMedia(isMain ? "main" : item.src)
                                }
                                sx={{
                                  borderRadius: "0px",
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  border: isActive
                                    ? "2px solid #323232"
                                    : "2px solid transparent",
                                  position: "relative",
                                  width: "100%",
                                  height: `${THUMB_HEIGHT}px`,
                                  maxHeight: `${THUMB_HEIGHT}px`,
                                  boxSizing: "border-box",
                                  flexShrink: 0,
                                  transition: "border-color 0.2s",
                                  "&:hover": { border: "2px solid #888" },
                                }}>
                                <img
                                  src={item.src}
                                  alt={`media-${globalIndex}`}
                                  width="100%"
                                  height="100%"
                                  decoding="async"
                                  loading="eager"
                                  style={{
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      {canScroll && (
                        <Box
                          onClick={() =>
                            canScrollDown && setSliderStart((p) => p + 1)
                          }
                          sx={{
                            cursor: canScrollDown ? "pointer" : "default",
                            opacity: canScrollDown ? 1 : 0.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#f0f0f0",
                            transition: "opacity 0.2s, background-color 0.2s",
                            "&:hover": canScrollDown
                              ? { backgroundColor: "#e0e0e0" }
                              : {},
                          }}>
                          <KeyboardArrowDownIcon
                            sx={{ fontSize: 20, color: "#323232" }}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              <Box
                onMouseLeave={handleZoomWrapperLeave}
                sx={{
                  position: "relative",
                  zIndex: zoomActive ? 12 : 1,
                  display: "flex",
                  alignItems: "flex-start",
                  flexShrink: 0,
                }}>
                <Box
                  ref={imageContainerRef}
                  onClick={handleImageClick}
                  onMouseMove={(e) => {
                    if (zoomActive && isMdUp) updateLensFromEvent(e);
                  }}
                  sx={{
                    padding: 0,
                    position: "relative",
                    overflow: "hidden",
                    height: "400px",
                    width: "100%",
                    maxWidth: { xs: "200px", sm: "400px" },
                    placeContent: "center",
                    borderRadius: "0px",
                    cursor: isMdUp ? "zoom-in" : "default",
                  }}>
                  <img
                    width="100%"
                    height="auto"
                    src={mainImageSrc}
                    alt={`Product ${product?.slug}`}
                    onLoad={handleImageLoad}
                    decoding="async"
                    fetchpriority="high"
                    style={{
                      display: "block",
                      height: "100%",
                      objectFit: "contain",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  />

                  {zoomActive &&
                    isMdUp &&
                    imageRect &&
                    naturalSize.w > 0 &&
                    (() => {
                      const ir = imageRect;
                      const lensW = ir.rw * LENS_SIZE_FRAC;
                      const lensH = ir.rh * LENS_SIZE_FRAC;
                      return (
                        <Box
                          sx={{
                            position: "absolute",
                            left: lensCenter.cx - lensW / 2,
                            top: lensCenter.cy - lensH / 2,
                            width: lensW,
                            height: lensH,
                            border: "1.5px solid rgba(255,255,255,0.95)",
                            boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
                            pointerEvents: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      );
                    })()}

                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: "absolute",
                      top: "20px",
                      right: "20px",
                      zIndex: 10,
                    }}>
                    <Box
                      onClick={() => handleToggleBookmark(product)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isBookmarked(product?.post_id)
                          ? "#323232"
                          : "#fff",
                        borderRadius: "50%",
                        padding: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "transform 0.2s ease",
                        "&:hover": { transform: "scale(1.1)" },
                      }}>
                      <BookmarkBorderIcon
                        sx={{
                          color: isBookmarked(product?.post_id)
                            ? "#fff"
                            : "#323232",
                          fontSize: 24,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {zoomActive &&
                  isMdUp &&
                  imageRect &&
                  naturalSize.w > 0 &&
                  mainImageSrc && (
                    <Box
                      sx={{
                        display: { xs: "none", md: "block" },
                        position: "absolute",
                        left: "100%",
                        top: 0,
                        ml: "18px",
                        width: "400px",
                        height: "400px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        pointerEvents: "none",
                      }}>
                      <img
                        src={mainImageSrc}
                        alt=""
                        draggable={false}
                        style={{
                          position: "absolute",
                          width: imageRect.rw * ZOOM_SCALE,
                          height: imageRect.rh * ZOOM_SCALE,
                          left:
                            200 -
                            (lensCenter.cx - imageRect.ox) * ZOOM_SCALE,
                          top:
                            200 -
                            (lensCenter.cy - imageRect.oy) * ZOOM_SCALE,
                          maxWidth: "none",
                          maxHeight: "none",
                          objectFit: "fill",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                  )}
              </Box>
            </Box>
            <Box
              sx={{
                paddingLeft: { xs: "0", md: "32px" },
                height: "100%",
              }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: "30px",
                  lineHeight: "43px",
                  color: "#000000",
                  fontWeight: 400,
                  letterSpacing: "0.04em",
                  marginBottom: "32px",
                  fontFamily: "Avenir Medium",
                }}>
                {product?.name}
              </Typography>

              <Box
                sx={{
                  maxHeight: "320px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  height: "100%",
                }}>
                {sizeDisplay.kind === "list" ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      gap: "8px",
                      width: "100%",
                    }}>
                    {sizeDisplay.items.map((size, index) => (
                      <Typography
                        key={`${size}-${index}`}
                        sx={{
                          fontSize: "16px",
                          lineHeight: "24px",
                          color: "#000000",
                          fontWeight: 400,
                          fontFamily: "Avenir Book",
                        }}>
                        {formatSizeLineDisplay(size)}
                      </Typography>
                    ))}
                  </Box>
                ) : sizeDisplay.kind === "detail" ? (
                  <Typography
                    sx={{
                      fontSize: "16px",
                      lineHeight: "24px",
                      color: "#000000",
                      fontWeight: 400,
                      fontFamily: "Avenir Book",
                      whiteSpace: "pre-line",
                    }}>
                    {sizeDisplay.text
                      .split(/\r?\n/)
                      .map((line) => formatSizeLineDisplay(line))
                      .join("\n")}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Box>
      {relatedProducts?.length > 0 ? (
        <Box sx={{ backgroundColor: "#F9F9F9" }}>
          <Box sx={{ maxWidth: "1440px", margin: "0 auto" }}>
            <Grid
              container
              spacing={2}
              sx={{ paddingInline: { xs: "16px", md: "60px" } }}>
              <Grid
                item
                sx={{ paddingTop: "48px" }}
                xs={12}
                sm={12}
                md={12}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center">
                <Typography
                  sx={{
                    fontSize: "24px",
                    lineHeight: "28px",
                    color: "#2B2B2B",
                    fontWeight: 500,
                    marginBottom: { xs: "20px", md: "30px" },
                    letterSpacing: "0.04em",
                    paddingTop: { xs: "5px", md: "10px" },
                    fontFamily: "Avenir Medium",
                    textAlign: "center",
                  }}>
                  Complete the Look
                </Typography>
                <Box
                  sx={{
                    marginBottom: "40px",
                    display: "inline-flex",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: { xs: 2, md: 3 },
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    overflowY: "hidden",
                    marginLeft: "auto",
                    marginRight: "auto",
                    px: { xs: 2, md: 0 },
                    width: "100%",
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      justifyContent: "center",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}>
                    {relatedProducts.slice(0, 4).map((rp, index) => (
                      <Box
                        key={index}
                        sx={{
                          flex: "0 0 auto",
                          width: { xs: "140px", md: "160px" },
                        }}>
                        <Box
                          sx={{
                            width: "100%",
                            height: { xs: "140px", md: "160px" },
                            overflow: "hidden",
                            borderRadius: "0px",
                            mb: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            position: "relative",
                            cursor: "pointer",
                            "&:hover .overlay": { opacity: 1 },
                          }}
                          onClick={() => navigate(`/products/${rp?.post_id}`)}>
                          <img
                            style={{
                              borderRadius: "0px",
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                            src={rp?.featured_image}
                            alt={`Product ${index}`}
                            decoding="async"
                            loading="lazy"
                          />
                          <Box
                            className="overlay"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0, 0, 0, 0.2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                              borderRadius: "0px",
                            }}>
                            <Button
                              variant="contained"
                              sx={{
                                backgroundColor: "#333333",
                                color: "#fff",
                                padding: "8px 12px",
                                borderRadius: "0px",
                                fontSize: "14px",
                                fontWeight: 400,
                                textTransform: "none",
                                fontFamily: "Avenir Book",
                                "&:hover": { backgroundColor: "#333" },
                              }}>
                              View Product
                            </Button>
                          </Box>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleBookmark(rp);
                            }}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isBookmarked(rp?.post_id)
                                ? "#323232"
                                : "#fff",
                              borderRadius: "50%",
                              padding: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              transition: "transform 0.2s ease",
                              position: "absolute",
                              top: "10px",
                              right: "10px",
                              zIndex: 1000,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.transform = "scale(1.1)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }>
                            <BookmarkBorderIcon
                              sx={{
                                color: isBookmarked(rp?.post_id)
                                  ? "#fff"
                                  : "#323232",
                                fontSize: 20,
                              }}
                            />
                          </div>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "18px",
                            fontFamily: "Avenir Book",
                            lineHeight: "22px",
                            marginTop: "10px",
                            color: "#000000",
                            fontWeight: 500,
                          }}>
                          {rp?.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export default SingleProductPage;
